import React, { useEffect, useMemo, useCallback, useState, type ReactNode } from 'react'
import { Outlet, Scripts, ScrollRestoration, Meta, Links, useLocation } from 'react-router'
import appsignal from './appsignal'
import { ErrorBoundary as AppSignalErrorBoundary } from '@appsignal/react'
import type { Span } from '@appsignal/javascript'
import { useAuthStore } from '@/stores/authStore'
import { useMessageStore } from '@/stores/messageStore'
import NavBar from '@/components/NavBar'
import Footer from '@/components/Footer'
import LoadingIndicator from '@/components/LoadingIndicator'
import FloatingMessageDisplay from '@/components/Message/FloatingMessageDisplay'
import InlineMessage from '@/components/Message/InlineMessage'
import AppInit from '@/components/AppInit'
import useVersionChecker from '@/hooks/useVersionChecker'
import { applyAppUpdate } from '@/utils/applyAppUpdate'
import { useThemeStore, THEME_KEY } from '@/stores/themeStore'
import './App.css'
import './index.css'

interface LayoutProps {
  children: ReactNode
}

// Runs before hydration to prevent a light-mode flash on refresh.
const themeInitScript = `(function(){try{var k=${JSON.stringify(THEME_KEY)};var p=localStorage.getItem(k)||'auto';var e=p==='auto'?(window.matchMedia('(prefers-color-scheme: dark)').matches?'dark':'light'):p;var r=document.documentElement;r.setAttribute('data-theme',e);r.style.colorScheme=e;}catch(_){}})();`

export function Layout({ children }: LayoutProps): React.ReactElement {
  const effective = useThemeStore((s) => s.effective)

  return (
    <html lang="nl" data-theme={effective} suppressHydrationWarning>
      <head>
        <meta charSet="UTF-8" />
        <script dangerouslySetInnerHTML={{ __html: themeInitScript }} />
        <link rel="manifest" href="/manifest.webmanifest" />
        <link rel="icon" href="/favicon.ico" sizes="32x32" />
        <link rel="icon" href="/icon.svg" type="image/svg+xml" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black" />
        <meta name="apple-mobile-web-app-title" content="TMC" />
        <link rel="apple-touch-icon" href="/apple-touch-icon.png" />
        <script src="/env.js"></script>
        <title>Persoonlijke migrainezorg | The Migraine Clinic</title>
        <Meta />
        <Links />
      </head>
      <body>
        <div className="app-shell">{children}</div>
        <div id="x-uuid" data-version={__BUILD_ID__} />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  )
}

const FallbackComponent: React.FC = () => {
  const showMessage = useMessageStore((s) => s.showMessage)

  useEffect(() => {
    showMessage('Er is een onverwachte fout opgetreden. Probeer de pagina te herladen.', {
      type: 'error',
      duration: null,
      closable: true,
      toast: true,
    })
  }, [showMessage])

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 p-6 text-center">
      <p className="text-text">Er is een onverwachte fout opgetreden.</p>
      <button
        className="bg-purple text-white px-5 py-2 rounded-lg hover:bg-purple-dark transition-colors"
        onClick={() => window.location.reload()}
      >
        Pagina herladen
      </button>
    </div>
  )
}

export default function Root(): React.ReactElement | null {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const isLoading = useAuthStore((s) => s.isLoading)
  const location = useLocation()
  const effective = useThemeStore((s) => s.effective)
  const [hydrated, setHydrated] = useState(false)
  // De service worker precachet de app-shell, dus een client kan op een oude
  // bundle blijven hangen zonder het te weten. Zonder deze melding krijgt zo
  // iemand nooit een fix te zien.
  const isOutdated = useVersionChecker()

  useEffect(() => { setHydrated(true) }, [])

  const tags = useMemo<Record<string, string>>(() => ({ tag: 'value' }), [])
  const fallback = useCallback(() => <FallbackComponent />, [])
  const override = useCallback(
    (span: Span) => {
      span.setTags({ path: location.pathname })
      span.setParams({ url: window.location.href })
      return span
    },
    [location.pathname]
  )

  useEffect(() => {
    document.body.classList.toggle('has-navbar', isAuthenticated)
  }, [isAuthenticated])

  // AppInit must mount regardless of isLoading: it triggers the bootstrap()
  // whoami probe that flips isLoading to false. Gating it behind !isLoading
  // (as before) deadlocks the app on a blank loading screen.
  const content = (
    <>
      <AppInit />

      {isLoading ? (
        <LoadingIndicator />
      ) : (
        <>
          <NavBar key={location.pathname} />

          {hydrated && !isAuthenticated && (
            <header className="app-header">
              <img
                src={effective === 'dark'
                  ? './logo-horizontaal-soft-beige.svg'
                  : './logo-horizontaal-moss-green.svg'
                }
                alt="logo the migraine clinic"
                className="logo-login"
              />
            </header>
          )}

          {/* Zelfde breedte als .page-wrapper, zodat banners uitlijnen met de
              kaarten eronder in plaats van over de volle schermbreedte te lopen. */}
          <div className="w-full max-w-[860px] mx-auto px-4">
            {isOutdated && (
              <div className="card card-available card-update cursor-default mb-3" data-testid="app-outdated-banner">
                <p className="mb-3">Er is een nieuwe versie van de app beschikbaar. Klik op de knop om de nieuwe versie op te halen</p>
                <button
                  className="bg-purple text-white px-5 py-2 rounded-lg hover:bg-purple-dark transition-colors min-h-[44px]"
                  onClick={() => { void applyAppUpdate() }}
                >
                  Nu bijwerken
                </button>
              </div>
            )}

            <InlineMessage />
          </div>
          <Outlet />
          <FloatingMessageDisplay />
          <Footer />
        </>
      )}
    </>
  )

  if (!appsignal) return content

  return (
    <AppSignalErrorBoundary
      instance={appsignal}
      action="App"
      tags={tags}
      override={override}
      fallback={fallback}
    >
      {content}
    </AppSignalErrorBoundary>
  )
}

export function HydrateFallback(): React.ReactElement {
  return <LoadingIndicator />
}
